import { type ChangeEvent } from 'react';

import { capitalizeFirstLetter } from '@eneco/common/string';
import { InputDate, InputText, RadioButton, RadioGroup } from '@eneco/sparky';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
  type PathValue,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';

import { addLeadingZero, formatInitials } from '../../helpers/format';

type TextFieldContent = {
  label?: string;
  placeholder?: string;
  hint?: string;
};

type SalutationField = {
  label?: string;
  options?: {
    name: string;
    label: string;
  }[];
};

type PersonalDetailsTextFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  name: TName;
  register: UseFormRegister<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  error?: string;
  field?: TextFieldContent;
  transform?: 'capitalize' | 'initials';
};

type BirthdateFormValues = FieldValues & {
  day: string | number;
  month: string | number;
  year: string | number;
};

type PersonalDetailsBirthdateProps<TFieldValues extends BirthdateFormValues> = {
  register: UseFormRegister<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  error?: string;
  hasDayError: boolean;
  hasMonthError: boolean;
  hasYearError: boolean;
  birthdate?: TextFieldContent;
  day?: TextFieldContent;
  month?: TextFieldContent;
  year?: TextFieldContent;
};

const formatFieldValue = (transform: 'capitalize' | 'initials', event: ChangeEvent<HTMLInputElement>) => {
  if (transform === 'initials') return formatInitials(event.target.value);

  return capitalizeFirstLetter(event?.target?.value?.trim());
};

export const PersonalDetailsSalutation = <TFieldValues extends FieldValues & { salutation: string }>({
  control,
  error,
  field,
}: {
  control: Control<TFieldValues>;
  error?: string;
  field?: SalutationField;
}) => (
    <Controller
    control={control}
    name={'salutation' as FieldPath<TFieldValues>}
    render={({ field: { onChange, value, name } }) => (
      <RadioGroup
        label={field?.label ?? ''}
        name={name}
        direction="row"
        value={value}
        onValueChange={onChange}
        error={error}>
        {field?.options?.map(option => (
          <RadioButton value={option.name} key={option.name}>
            {option.label}
          </RadioButton>
        ))}
      </RadioGroup>
    )}
  />
);

export const PersonalDetailsTextField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  name,
  register,
  setValue,
  error,
  field,
  transform,
}: PersonalDetailsTextFieldProps<TFieldValues, TName>) => (
  <InputText
    {...register(
      name,
      transform
        ? {
            onBlur: (event: ChangeEvent<HTMLInputElement>) => {
              setValue(name, formatFieldValue(transform, event) as PathValue<TFieldValues, TName> & string);
            },
          }
        : undefined,
    )}
    label={field?.label ?? ''}
    placeholder={field?.placeholder}
    hint={field?.hint}
    error={error}
  />
);

export const PersonalDetailsBirthdate = <TFieldValues extends BirthdateFormValues>({
  register,
  setValue,
  error,
  hasDayError,
  hasMonthError,
  hasYearError,
  birthdate,
  day,
  month,
  year,
}: PersonalDetailsBirthdateProps<TFieldValues>) => (
  <InputDate label={birthdate?.label ?? ''} hint={birthdate?.hint} name="birthdate" error={error}>
    <InputDate.Day
      {...register('day' as FieldPath<TFieldValues>, {
        onBlur: (event: ChangeEvent<HTMLInputElement>) => {
          if (event.target.value) {
            setValue(
              'day' as FieldPath<TFieldValues>,
              addLeadingZero(event.target.value) as PathValue<TFieldValues, FieldPath<TFieldValues>> & string,
            );
          }
        },
      })}
      label={day?.label ?? ''}
      placeholder={day?.placeholder}
      hasError={hasDayError}
    />
    <InputDate.Day
      {...register('month' as FieldPath<TFieldValues>, {
        onBlur: (event: ChangeEvent<HTMLInputElement>) => {
          if (event.target.value) {
            setValue(
              'month' as FieldPath<TFieldValues>,
              addLeadingZero(event.target.value) as PathValue<TFieldValues, FieldPath<TFieldValues>> & string,
            );
          }
        },
      })}
      label={month?.label ?? ''}
      placeholder={month?.placeholder}
      hasError={hasMonthError}
    />
    <InputDate.Day
      {...register('year' as FieldPath<TFieldValues>)}
      label={year?.label ?? ''}
      placeholder={year?.placeholder}
      hasError={hasYearError}
    />
  </InputDate>
);
